export type PropFilter = <T, K extends keyof T>(array: T[], prop: K) => Array<T[K]>;

declare global {
    // eslint-disable-next-line @typescript-eslint/no-namespace
    namespace angular {
        interface IFilterService {
            // eslint-disable-next-line @typescript-eslint/prefer-function-type
            (name: 'prop'): PropFilter;
        }
    }
}

// @formatter:off
/**
 * @ngdoc filter
 * @name prop
 * @module portaro.filters
 * @kind function
 *
 * @param {Array} arr Input array
 * @param {string} prop Name of property
 *
 * @return {Array} Filtered properties
 *
 * @description
 * Filter gets property from each array element (object) and returns this properties in new array.
 *
 * ```javascript
 *  type PropFilter = <T, K extends keyof T>(array: T[], prop: K) => Array<T[K]>;
 * ```
 *
 * @example
 * ```
 * <example module="propFilterExample" name="propFilterExample" frame-no-resize="true">
 * <file name="index.html">
 *      <main ng-controller="propExampleController as pec">
 *          <div><label for="source">Source:</label></div>
 *          <div>
 *              <textarea id="source" ng-value="pec.menuItems | json"></textarea>
 *          </div>
 *          <div><label for="output">Output:</label></div>
 *          <div>
 *              <textarea id="output" ng-value="pec.menuItems | prop : 'text' | json"></textarea>
 *          </div>
 *      </main>
 *  </file>
 *  <file name="script.js">
 *      angular.module('propFilterExample', ['portaro.filters'])
 *          .controller('propExampleController', class {
 *              constructor() {
 *                  this.menuItems = [
 *                      {
 *                          text: 'Home',
 *                          url: '/home'
 *                      },
 *                      {
 *                          text: 'My Account',
 *                          url: '/account'
 *                      }
 *                  ];
 *              }
 *          });
 *  </file>
 * </example>
 * ```
 */
 // @formatter:on
export default function propFilter(): PropFilter {
    return <T, K extends keyof T>(arr: T[], prop: K): Array<T[K]> => {
        const props: Array<T[K]> = [];

        if (arr) {
            arr.forEach((item) => {
                if (typeof (item[prop]) !== 'undefined') {
                    props.push(item[prop]);
                }
            });
        }
        return props;
    };
}

propFilter.filterName = 'prop';
